import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { usePosts } from '@/hooks/usePosts';
import { useCategories } from '@/hooks/useCategories';
import { supabase } from '@/lib/supabase';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import CatLoader from '@/components/base/CatLoader';
import ReactionBar from './components/ReactionBar';
import AdSenseAd from '@/components/ads/AdSenseAd';
import Seo, { SITE_URL } from '@/components/base/Seo';

export default function PostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getPostBySlug, getPublishedPosts, loading } = usePosts();
  const post = slug ? getPostBySlug(slug) : undefined;
  const allPosts = getPublishedPosts();
  const { getCategoryById } = useCategories();
  const primaryCatId = post?.categories?.[0];
  const catInfo = primaryCatId ? getCategoryById(primaryCatId) : undefined;

  // Track post view
  useEffect(() => {
    if (!post?.id) return;
    const trackView = async () => {
      try {
        await supabase.from('post_views').insert({ post_id: post.id });
      } catch {
        // silent
      }
    };
    trackView();
  }, [post?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col font-body">
        <Navbar />
        <CatLoader fullPage message="Preparando o artigo..." />
        <Footer popularPosts={[]} />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center py-20 text-foreground-500">
            <i className="ri-file-unknow-line text-5xl mb-4 block"></i>
            <p className="text-lg font-bold mb-4">Artigo não encontrado.</p>
            <Link to="/blog" className="text-primary-500 font-bold cursor-pointer">
              Ver todos os artigos
            </Link>
          </div>
        </div>
        <Footer popularPosts={allPosts} />
      </div>
    );
  }

  const relatedPosts = allPosts
    .filter(p => p.id !== post.id && p.categories.some(c => post.categories.includes(c)))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Seo
        title={post.title}
        description={post.subtitle || `Leia ${post.title} no Miau de Casa.`}
        path={`/post/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.subtitle,
          image: post.image,
          datePublished: post.createdAt,
          dateModified: post.createdAt,
          inLanguage: 'pt-BR',
          mainEntityOfPage: `${SITE_URL}/post/${post.slug}`,
          author: { '@type': 'Organization', name: 'Miau de Casa' },
          publisher: { '@type': 'Organization', name: 'Miau de Casa', url: SITE_URL },
        }}
      />
      <Navbar />

      <div>
        {/* Hero Image */}
        <div className="w-full h-[200px] sm:h-[260px] md:h-[400px] lg:h-[460px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Article Content */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Link to="/" className="text-foreground-400 text-sm hover:text-primary-500 cursor-pointer">Início</Link>
            <span className="text-foreground-300">/</span>
            {post.categories.map(id => {
              const c = getCategoryById(id);
              return c ? (
                <Link key={id} to={`/categoria/${id}`} className="text-foreground-400 text-sm hover:text-primary-500 cursor-pointer transition-colors">{c.label}</Link>
              ) : null;
            })}
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-600 text-sm line-clamp-1">{post.title}</span>
          </div>

          {/* Category badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {post.categories.map(id => {
              const cat = getCategoryById(id);
              return cat ? (
                <Link key={id} to={`/categoria/${id}`} className="inline-block bg-primary-100 text-primary-600 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full hover:bg-primary-200 transition-colors cursor-pointer">
                  {cat.label}
                </Link>
              ) : null;
            })}
          </div>
          <h1 className="font-heading font-black text-foreground-950 text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-3 md:mb-4">
            {post.title}
          </h1>
          <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-3 md:mb-4">{post.subtitle}</p>
          <div className="flex items-center gap-4 text-foreground-400 text-sm mb-4">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {post.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>{' '}
              {new Date(post.createdAt).toLocaleDateString('pt-BR', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </span>
          </div>

          {/* Reaction Bar */}
          <div className="pb-6 mb-6 border-b border-background-200">
            <ReactionBar postId={post.id} postTitle={post.title} />
          </div>

          <AdSenseAd placement="articleTop" className="mb-8 md:mb-10" />

          {/* Article Body */}
          <div className="space-y-6">
            {post.bodyHtml ? (
              <div
                className="article-body prose-custom"
                dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
              />
            ) : (
              post.content.map((block, i) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={i} className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl mt-8">
                      {block.content}
                    </h2>
                  );
                }
                if (block.type === 'subheading') {
                  return (
                    <h3 key={i} className="font-heading font-bold text-foreground-900 text-xl md:text-2xl mt-6">
                      {block.content}
                    </h3>
                  );
                }
                if (block.type === 'image') {
                  return (
                    <div key={i} className="w-full rounded-lg overflow-hidden my-6">
                      <img
                        src={block.content}
                        alt="Ilustração do artigo"
                        className="w-full object-cover object-top"
                      />
                    </div>
                  );
                }
                if (block.type === 'html') {
                  return (
                    <div
                      key={i}
                      className="my-6"
                      dangerouslySetInnerHTML={{ __html: block.content }}
                    />
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote
                      key={i}
                      className="border-l-4 border-l-primary-400 bg-background-100 rounded-r-lg px-6 py-4 my-6 italic text-foreground-700 text-lg leading-relaxed"
                    >
                      {block.content}
                    </blockquote>
                  );
                }
                return (
                  <p key={i} className="text-foreground-700 text-base md:text-lg leading-relaxed">
                    {block.content}
                  </p>
                );
              })
            )}
          </div>

          <AdSenseAd placement="articleBottom" className="mt-8 md:mt-10" />

          {/* Bottom Reaction Bar */}
          <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-background-200">
            <p className="text-foreground-500 text-sm font-bold mb-3">Gostou do artigo? Deixe sua reação:</p>
            <ReactionBar postId={post.id} postTitle={post.title} />
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10 border-t border-background-200">
            <h2 className="font-heading font-bold text-foreground-950 text-lg sm:text-xl md:text-2xl mb-6 md:mb-8">
              Artigos relacionados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {relatedPosts.map((p, i) => (
                <div
                  key={p.id}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'both' }}
                >
                  <ArticleCard post={p} variant="compact" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer popularPosts={allPosts} />

      <style>{`
        .article-body h2 {
          font-family: var(--font-heading, inherit);
          font-size: 1.25rem;
          font-weight: 700;
          margin-top: 1.75rem;
          margin-bottom: 0.75rem;
          line-height: 1.3;
          color: oklch(var(--foreground-950));
        }
        .article-body h3 {
          font-family: var(--font-heading, inherit);
          font-size: 1.1rem;
          font-weight: 700;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          line-height: 1.4;
          color: oklch(var(--foreground-900));
        }
        .article-body p {
          margin-bottom: 1rem;
          line-height: 1.7;
          font-size: 1rem;
          color: oklch(var(--foreground-700));
        }
        @media (min-width: 768px) {
          .article-body h2 { font-size: 1.5rem; margin-top: 2rem; }
          .article-body h3 { font-size: 1.25rem; margin-top: 1.5rem; }
          .article-body p { font-size: 1.0625rem; }
        }
        .article-body ul,
        .article-body ol {
          padding-left: 1.25rem;
          margin-bottom: 1rem;
          line-height: 1.75;
          color: oklch(var(--foreground-700));
        }
        @media (min-width: 768px) {
          .article-body ul,
          .article-body ol { padding-left: 1.5rem; }
        }
        .article-body ul {
          list-style-type: disc;
        }
        .article-body ol {
          list-style-type: decimal;
        }
        .article-body li {
          margin-bottom: 0.5rem;
          font-size: 1rem;
        }
        @media (min-width: 768px) {
          .article-body li { font-size: 1.0625rem; }
        }
        .article-body blockquote {
          border-left: 4px solid oklch(var(--primary-500));
          padding: 0.75rem 1rem;
          margin: 1.25rem 0;
          font-style: italic;
          font-size: 1rem;
          line-height: 1.7;
          color: oklch(var(--foreground-700));
          background: oklch(var(--background-100) / 0.6);
          border-radius: 0 0.5rem 0.5rem 0;
        }
        @media (min-width: 768px) {
          .article-body blockquote { padding: 0.75rem 1.25rem; margin: 1.5rem 0; font-size: 1.125rem; }
        }
        .article-body a {
          color: oklch(var(--primary-500));
          text-decoration: underline;
        }
        .article-body img {
          max-width: 100%;
          border-radius: 0.5rem;
          margin: 1.25rem 0;
        }
        @media (min-width: 768px) {
          .article-body img { margin: 1.5rem 0; }
        }
        .article-body pre {
          background: oklch(var(--background-100));
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1.25rem 0;
          font-size: 0.8rem;
          line-height: 1.6;
          color: oklch(var(--foreground-800));
        }
        @media (min-width: 768px) {
          .article-body pre { padding: 1rem 1.25rem; margin: 1.5rem 0; font-size: 0.9rem; }
        }
        .article-body strong {
          font-weight: 700;
          color: oklch(var(--foreground-900));
        }
      `}</style>
    </div>
  );
}
